import { isMatch } from 'lodash';
import type { PathsWithMethod } from 'openapi-typescript-helpers';
import {
  createMutateHook,
  createQueryHook,
  type TypesForGetRequest,
} from 'swr-openapi';
import { aiApi, auditApi, coreApi, identityApi, paymentApi } from './clients';
import type {
  AuditSchema,
  CoreSchema,
  IdentitySchema,
  PaymentSchema,
} from './generated';
import { createKeyMatcher } from './swr/createKeyMatcher';

/**
 * Hook for fetching from audit-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useAudit = createQueryHook(auditApi, 'audit-api');

/**
 * Hook for fetching from identity-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useIdentity = createQueryHook(identityApi, 'identity-api');

/**
 * Type of identity-service get request object based on path
 * @example
 * type UserRequest = IdentityRequest<'/users/{userId}'>
 */
export type IdentityRequest<
  Path extends PathsWithMethod<IdentitySchema.paths, 'get'>,
> = TypesForGetRequest<IdentitySchema.paths, Path>;

// ------------------------------
// Core
// ------------------------------
/**
 * Hook for fetching from core-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useCore = createQueryHook(coreApi, 'core-api');

/**
 * Type of audit-service get request object based on path
 * @example
 * type AuditRequest = AuditRequest<'/audits'>
 */
export type AuditRequest<
  Path extends PathsWithMethod<AuditSchema.paths, 'get'>,
> = TypesForGetRequest<AuditSchema.paths, Path>;

/**
 * Hook for mutating from audit-service.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useAuditMutate = createMutateHook(auditApi, 'audit-api', isMatch);

/**
 * Hook for mutating from core-service.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useCoreMutate = createMutateHook(coreApi, 'core-api', isMatch);

/**
 * Builds a typed SWR key predicate for core-service, for callers that can't
 * reach `useCoreMutate`. Pass the result to `mutate` from `swr`.
 */
export const matchCoreKey = createKeyMatcher<CoreSchema.paths>(
  'core-api',
  isMatch,
);

/**
 * Type of core-service get request object based on path
 * @example
 * type CoreRequest = CoreRequest<'/users/{userId}'>
 */
export type CoreRequest<Path extends PathsWithMethod<CoreSchema.paths, 'get'>> =
  TypesForGetRequest<CoreSchema.paths, Path>;

/**
 * Hook for fetching from payment-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const usePayment = createQueryHook(paymentApi, 'payment-api');

/**
 * Type of payment-service get request object based on path
 * @example
 * type PaymentRequest = PaymentRequest<'/users/{userId}'>
 */
export type PaymentRequest<
  Path extends PathsWithMethod<PaymentSchema.paths, 'get'>,
> = TypesForGetRequest<PaymentSchema.paths, Path>;
/**
 * Hook for fetching from ai-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useAI = createQueryHook(aiApi, 'ai-api');
